import {
  AppShell,
  type AppShellNavbarProps,
  Box,
  Divider,
  Kbd,
  NavLink,
  type NavLinkProps,
  Title,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core"
import { useOs } from "@mantine/hooks"
import { spotlight } from "@mantine/spotlight"
import Link from "next/link"
import { useTranslation } from "react-i18next"
import { ActivityIcon, AdminOnly, icons, Logo } from "@/components"
import { useConfig } from "@/hooks"
import { useAsPath } from "@/hooks/useAsPath"
import type { TActivityType } from "@/types"
import { buildActivityURL, companySettingsURL, peopleURL, profileURL } from "@/utils"
import { NavLanguage } from "./Language"
import classes from "./SideMenu.module.css"
import { SuperNav } from "./SuperNav"

type TProps = {
  xs?: boolean
  hidden?: boolean
  collapsed?: boolean
  onToggleCollapse?: () => void
}

const misc: TActivityType[] = ["anniversary", "recognition", "coffee", "radio", "reimbursement", "vacation"]
const labels: Partial<Record<TActivityType, string>> = {
  anniversary: "Anniversaries",
  coffee: "Coffee Chats",
  reimbursement: "Reimbursements",
  vacation: "Vacations",
}

export function SideMenu({
  xs,
  hidden,
  collapsed,
  onToggleCollapse,
  ...props
}: TProps & Omit<AppShellNavbarProps, "children">) {
  const { t } = useTranslation()
  const config = useConfig()
  const { sections = [] } = config || {}
  const asPath = useAsPath()
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const os = useOs()
  const modButton = os === "macos" ? "Cmd" : "Ctrl"

  const disabledSections = Object.values(sections).filter((section) => !section.is_active)

  if (!config) return null

  return (
    <AppShell.Navbar pb="md" hidden={hidden} style={{ overflowY: "auto" }} {...props}>
      {!xs && !collapsed && (
        <Title order={4}>
          <Logo my="xs" pl="sm" />
        </Title>
      )}
      <Box mb="auto">
        <SideLink url={profileURL} label={t("Profile")} icon={<icons.Profile />} path={asPath} collapsed={collapsed} />

        <SideLink url={peopleURL} label={t("People")} icon={<icons.Team />} path={asPath} collapsed={collapsed} />

        {/* Surveys */}
        {!collapsed && <NavLink disabled pb={0} label={t("Surveys")} classNames={classes} data-collapsed={collapsed} />}

        <SideLink
          url={buildActivityURL({ type: "survey" })}
          label={t("Surveys")}
          icon={<icons.survey />}
          path={asPath}
          collapsed={collapsed}
        />

        <SideLink
          url={buildActivityURL({ type: "pulse" })}
          label={t("Pulses")}
          icon={<icons.pulse />}
          path={asPath}
          collapsed={collapsed}
        />

        <SideLink
          url={buildActivityURL({ type: "feedback" })}
          label={t("Team Feedback")}
          icon={<icons.feedback />}
          path={asPath}
          collapsed={collapsed}
        />

        <SideLink
          url={buildActivityURL({ type: "onboarding" })}
          label={t("Onboarding")}
          icon={<icons.onboarding />}
          path={asPath}
          collapsed={collapsed}
        />

        {/* Misc (enabled) */}
        {!collapsed && <NavLink disabled pb={0} label={t("Misc")} classNames={classes} data-collapsed={collapsed} />}
        {misc.map((type) => {
          const section = sections[type]
          if (!section?.is_active) return null
          return <ActivityLink key={type} type={type} collapsed={collapsed} />
        })}

        {/* Misc (hidden) */}

        {!collapsed && disabledSections.length > 0 && (
          <NavLink disabled pb={0} label={t("Hidden")} classNames={classes} data-collapsed={collapsed} />
        )}
        {misc.map((type) => {
          const section = sections[type]
          if (section?.is_active) return null
          return <ActivityLink key={type} type={type} collapsed={collapsed} dimmed />
        })}

        {/* Admin */}
        <SuperNav collapsed={collapsed} />
      </Box>

      <Box>
        <Tooltip label={t("Search")} position="right" disabled={!collapsed}>
          <NavLink
            label={collapsed ? undefined : t("Search")}
            leftSection={<icons.Search />}
            rightSection={collapsed ? undefined : <Kbd size="xs">{modButton} + K</Kbd>}
            onClick={() => spotlight.open()}
            classNames={classes}
            data-collapsed={collapsed}
          />
        </Tooltip>
        <NavLanguage collapsed={collapsed} />
        <Tooltip label={t("Toggle Theme")} position="right" disabled={!collapsed}>
          <NavLink
            label={collapsed ? undefined : t("Toggle Theme")}
            leftSection={colorScheme == "light" ? <icons.Dark /> : <icons.Light />}
            onClick={() => toggleColorScheme()}
            classNames={classes}
            data-collapsed={collapsed}
          />
        </Tooltip>
        <AdminOnly>
          <SideLink
            path={asPath}
            icon={<icons.Settings />}
            url={companySettingsURL}
            label={t("Company Settings")}
            collapsed={collapsed}
          />
        </AdminOnly>
        <SideLink path={asPath} icon={<icons.Info />} url="/faq" label={t("FAQ")} collapsed={collapsed} />
        {!xs && onToggleCollapse && (
          <Tooltip label={collapsed ? t("Expand") : t("Collapse")} position="right" disabled={!collapsed}>
            <NavLink
              component="button"
              label={collapsed ? undefined : t("Collapse")}
              leftSection={collapsed ? <icons.Right /> : <icons.Left />}
              onClick={onToggleCollapse}
              classNames={classes}
              data-collapsed={collapsed}
            />
          </Tooltip>
        )}
        {!collapsed && <Divider />}
        <Tooltip label={t("Logout")} position="right" disabled={!collapsed}>
          <NavLink
            label={collapsed ? undefined : t("Logout")}
            leftSection={<icons.Logout />}
            component={Link}
            href="/logout"
            classNames={classes}
            data-collapsed={collapsed}
          />
        </Tooltip>
      </Box>
    </AppShell.Navbar>
  )
}

function SideLink({
  url,
  icon,
  path,
  label,
  collapsed,
  ...props
}: {
  url: string
  label: string
  path?: string
  icon?: React.ReactNode
  collapsed?: boolean
} & NavLinkProps) {
  return (
    <Tooltip label={label} disabled={!collapsed} position="right">
      <NavLink
        {...props}
        href={url}
        label={collapsed ? undefined : label}
        component={Link}
        leftSection={icon}
        active={path?.startsWith(url)}
        classNames={classes}
        data-collapsed={collapsed}
      />
    </Tooltip>
  )
}

function ActivityLink({ collapsed, dimmed, type }: { collapsed?: boolean; type: TActivityType; dimmed?: boolean }) {
  const { t } = useTranslation()
  const asPath = useAsPath()
  const label = t(labels[type] || type.charAt(0).toUpperCase() + type.slice(1))

  return (
    <SideLink
      label={label}
      path={asPath}
      collapsed={collapsed}
      url={buildActivityURL({ type })}
      c={dimmed ? "dimmed" : undefined}
      icon={<ActivityIcon type={type} />}
    />
  )
}
